// src/App.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Header from "./components/Header";
import CoachDetail from "./components/CoachDetail";
import CoachSignup from "./components/CoachSignup";
import Home from "./components/Home";
import CoachCards from "./components/CoachCards";
import About from "./components/About";
import TermsAndConditions from "./components/TermsAndConditions";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Footer from "./components/Footer";
import Contact from "./components/Contact";
import JoinUs from "./components/joinus";
import Login from "./components/Login";
import CoachReviews from "./components/CoachReviews";
import CoachGalleryads from "./components/CoachGalleryads";

import DashboardLayout from "./pages/coach/DashboardLayout";
import Profile from "./pages/coach/Profile";
import Availabilities from "./pages/coach/Availabilities";
import Bookings from "./pages/coach/Bookings";
import Settings from "./pages/coach/Settings";
import CoachGallery from "./pages/coach/CoachGallery";
import Subscription from "./pages/coach/Subscription";
import { Verification } from "./pages/coach/Verification";
import DashboardGifts from "./pages/coach/DashboardGifts";

import { SearchProvider } from "./components/SearchContext";
import ProtectedRoute from "./components/ProtectedRoute";

import ScrollToTop from "./components/ScrollToTop";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import VerifyEmail from "./components/VerifyEmail";

// PWA install context provider (named export)
import { InstallPromptProvider } from "./InstallPromptContext";
import BookingDetail from "./pages/coach/BookingDetail";

// Billing / Partners / Company
import { PartnerAdminPanel } from "./components/PartnerAdminPanel";
import { CompanyDashboard } from "./components/CompanyDashboard";
import GiftSuccess from "./pages/GiftSuccess";
import GiftCancel from "./pages/GiftCancel";
import GoogleAuthSuccess from "./pages/GoogleAuthSuccess";
import SubscriptionSuccess from "./pages/SubscriptionSuccess" ;
import SubscriptionCancel from "./pages/SubscriptionCancel";
import { HelmetProvider } from "react-helmet-async";




// Wrapper to read :partnerId and pass number to CompanyDashboard
function CompanyDashboardRoute() {
  const { partnerId } = useParams();
  const idNum = Number(partnerId);
  if (!partnerId || Number.isNaN(idNum)) {
    // fallback partner or redirect as desired
    return <Navigate to="/company/1" replace />;
  }
  return <CompanyDashboard partnerId={idNum} />;
}

const AppContent = ({ language, setLanguage }) => {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dashboard");
  const navigate = useNavigate();

  const [filters, setFilters] = useState({});
  const [specialty, setSpecialty] = useState("");

  const handleSearch = (selectedFilters, selectedSpecialty) => {
    setFilters(selectedFilters);
    setSpecialty(selectedSpecialty);

    navigate("/Coaches/Search", {
      state: { filters: selectedFilters, specialty: selectedSpecialty },
    });
  };

  return (
    <SearchProvider>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="min-h-screen bg-gray-50">
        {!isDashboard && <Header language={language} setLanguage={setLanguage} />}

        <main className={!isDashboard ? "container mx-auto px-4 py-6" : ""}>
          <Routes>
            {/* Public pages */}
              <Route path="/gift-success" element={<GiftSuccess />} />
              <Route path="/gift-cancel" element={<GiftCancel />} />
              <Route path="/coach/subscription/success" element={<SubscriptionSuccess />}/>
              <Route path="/coach/subscription/cancel"  element={<SubscriptionCancel />} />
              <Route path="/google-auth-success" element={<GoogleAuthSuccess />} />

            <Route
              path="/"
              element={<Home onSearch={handleSearch} filters={filters} specialty={specialty} />}
            />
            <Route path="/Coaches/Search" element={<CoachCards />} />
            <Route path="/search" element={<Navigate to="/Coaches/Search" replace />} />
            <Route path="/Login" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/coach/:slug" element={<CoachDetail />} />
            <Route path="/signup" element={<CoachSignup />} />
            <Route path="/coach-signup" element={<Navigate to="/signup" replace />} />
            <Route path="/About" element={<About />} />
            <Route path="/TermsAndConditions" element={<TermsAndConditions />} />
            <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
            <Route path="/Contact" element={<Contact />} />
            <Route path="/joinus" element={<JoinUs />} />
            <Route path="/CoachGalleryads" element={<CoachGalleryads />} />
            <Route path="/CoachReviews" element={<CoachReviews />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify" element={<VerifyEmail />} />

            {/* Pricing / Billing / Partners / Company */}
            <Route path="/pricing" element={<Navigate to="/dashboard/subscription" replace />} />
            <Route path="/admin/partners" element={<PartnerAdminPanel />} />
            <Route path="/company/:partnerId" element={<CompanyDashboardRoute />} />


            {/* Dashboard (protected + nested) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="profile" element={<Profile />} />
              <Route path="CoachGallery" element={<CoachGallery />} />
              <Route path="availabilities" element={<Availabilities />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="settings" element={<Settings />} />
              {/* nested route must be relative (no leading slash) */}
              <Route path="bookings/:id" element={<BookingDetail />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="verification" element={<Verification />} />
              <Route path="gifts" element={<DashboardGifts />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<div className="p-8">Not Found</div>} />
          </Routes>
        </main>

        {!isDashboard && <Footer />}
      </div>
    </SearchProvider>
  );
};

function App() {
  const [language, setLanguage] = useState("en");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

return (
  <div dir={language === "ar" ? "rtl" : "ltr"} lang={language}>
    <HelmetProvider>
      <InstallPromptProvider>
        <Router>
          <ScrollToTop />
          <AppContent language={language} setLanguage={setLanguage} />
        </Router>
      </InstallPromptProvider>
    </HelmetProvider>
  </div>
);

}

export default App;
